// ─────────────────────────────────────────────────────────────────────────────
// Notification sound player
//
// FIX: The previous implementation (kept commented below) attached a
// `canplaythrough` listener and only called `audio.play()` from inside it.
// For a cached / cloned audio element (which is exactly what happens on the
// 2nd+ notification, or when the browser already cached the file) that event
// has often ALREADY fired before the listener is attached — so the callback
// never ran, the returned promise never resolved, and NO SOUND played.
//
// The new implementation calls `audio.play()` directly (modern browsers return
// a promise and start playback as soon as enough data is buffered), resets
// `currentTime` so repeated notifications replay from the start, and unlocks
// audio on the first real user gesture to satisfy browser autoplay policies.
// ─────────────────────────────────────────────────────────────────────────────

/* ===========================================================================
 * OLD (disabled) implementation — kept for reference. Do not re-enable.
 * ---------------------------------------------------------------------------
 * class SoundPlayer {
 * 	constructor() {
 * 		this.audioCache = new Map();
 * 		this.currentlyPlaying = new Map();
 * 		this.audioUnlocked = false;
 * 		this.unlockAudio = this.unlockAudio.bind(this);
 * 		document.addEventListener('click', this.unlockAudio, { once: true });
 * 		document.addEventListener('keydown', this.unlockAudio, { once: true });
 * 	}
 *
 * 	unlockAudio() {
 * 		const audio = new Audio();
 * 		audio.src = '/assets/notification.mp3'; // <-- hardcoded, often 404s
 * 		audio.volume = 0;
 * 		audio.play().then(() => { audio.pause(); this.audioUnlocked = true; }).catch(() => {});
 * 	}
 *
 * 	play(src, options = {}) {
 * 		const { volume = 1, loop = false, id = src } = options;
 * 		// ...validation...
 * 		let audio = this.audioCache.has(src)
 * 			? this.audioCache.get(src).cloneNode()
 * 			: new Audio(src);
 * 		audio.volume = volume;
 * 		audio.loop = loop;
 * 		const playPromise = new Promise((resolve, reject) => {
 * 			const onCanPlay = () => {              // <-- BUG: may have already fired
 * 				audio.play().then(() => resolve(audio)).catch(reject);
 * 			};
 * 			audio.addEventListener('canplaythrough', onCanPlay, { once: true });
 * 			audio.addEventListener('error', onError, { once: true });
 * 			audio.load();                          // <-- resets readyState, race
 * 		});
 * 		return playPromise;
 * 	}
 * 	// stop / stopAll / preload / isPlaying / setVolume ...
 * }
 * =========================================================================== */

class SoundPlayer {
	constructor() {
		this.audioCache = new Map(); // src -> base HTMLAudioElement (cache/preload)
		this.currentlyPlaying = new Map(); // id  -> HTMLAudioElement
		this.audioUnlocked = false;

		this.unlockAudio = this.unlockAudio.bind(this);

		// Browsers block audio until the user has interacted with the page.
		// Prime playback on the first real interaction so later notifications
		// (triggered from socket events, not user gestures) can still sound.
		if (typeof document !== 'undefined') {
			document.addEventListener('click', this.unlockAudio);
			document.addEventListener('keydown', this.unlockAudio);
			document.addEventListener('touchstart', this.unlockAudio);
		}
	}

	// Resolve an import path / public path / URL to something <audio> can load.
	_resolveSrc(src) {
		if (typeof src !== 'string') return src;
		if (
			src.startsWith('http') ||
			src.startsWith('/') ||
			src.startsWith('blob:') ||
			src.startsWith('data:')
		) {
			return src;
		}
		return `/assets/${src}`;
	}

	unlockAudio() {
		if (this.audioUnlocked) return;
		this.audioUnlocked = true;

		// Warm up any already-cached sounds within this user gesture so the
		// browser treats later programmatic play() calls as user-allowed.
		this.audioCache.forEach((base) => {
			try {
				const warm = base.cloneNode(true);
				warm.volume = 0;
				const p = warm.play();
				if (p && typeof p.then === 'function') {
					p.then(() => {
						warm.pause();
						warm.currentTime = 0;
					}).catch(() => {});
				}
			} catch (e) {
				/* ignore */
			}
		});

		document.removeEventListener('click', this.unlockAudio);
		document.removeEventListener('keydown', this.unlockAudio);
		document.removeEventListener('touchstart', this.unlockAudio);
	}

	/**
	 * Play a notification sound.
	 * @param {string} src - Imported asset path, public path, or URL.
	 * @param {Object} [options]
	 * @param {number} [options.volume=1] - 0 to 1
	 * @param {boolean} [options.loop=false]
	 * @param {string} [options.id=src] - Unique id used to stop this sound.
	 * @returns {Promise<HTMLAudioElement|null>} audio element, or null if blocked/failed.
	 */
	play(src, options = {}) {
		const { volume = 1, loop = false, id = src } = options;

		if (!src) {
			console.warn('SoundPlayer.play: missing audio source');
			return Promise.resolve(null);
		}

		// Restart instead of overlapping when the same sound fires rapidly.
		this.stop(id);

		// Keep one cached base element per src; play on a fresh clone so
		// overlapping notifications don't fight over a single element.
		let base = this.audioCache.get(src);
		if (!base) {
			base = new Audio(this._resolveSrc(src));
			base.preload = 'auto';
			this.audioCache.set(src, base);
		}

		const audio = base.cloneNode(true);
		audio.volume = Math.max(0, Math.min(1, volume));
		audio.loop = loop;
		try {
			audio.currentTime = 0;
		} catch (e) {
			/* currentTime may be unavailable until metadata loads */
		}

		this.currentlyPlaying.set(id, audio);

		audio.addEventListener(
			'ended',
			() => {
				if (this.currentlyPlaying.get(id) === audio) {
					this.currentlyPlaying.delete(id);
				}
			},
			{ once: true },
		);

		// Play immediately. Do NOT wait for `canplaythrough` — for a cached
		// file it may already have fired, which is what broke the old code.
		let playPromise;
		try {
			playPromise = audio.play();
		} catch (error) {
			this.currentlyPlaying.delete(id);
			console.warn('SoundPlayer: play() threw:', error?.message || error);
			return Promise.resolve(null);
		}

		if (playPromise && typeof playPromise.then === 'function') {
			return playPromise
				.then(() => audio)
				.catch((error) => {
					this.currentlyPlaying.delete(id);
					// Autoplay blocked (no user gesture yet) or a load error.
					console.warn(
						'SoundPlayer: could not play notification sound:',
						error?.message || error,
					);
					return null;
				});
		}

		return Promise.resolve(audio);
	}

	/** Stop a specific sound by id. */
	stop(id) {
		const audio = this.currentlyPlaying.get(id);
		if (audio) {
			try {
				audio.pause();
				audio.currentTime = 0;
			} catch (e) {
				/* ignore */
			}
			this.currentlyPlaying.delete(id);
		}
	}

	/** Stop all currently playing sounds. */
	stopAll() {
		this.currentlyPlaying.forEach((audio, id) => {
			this.stop(id);
		});
	}

	/** Preload a sound into cache. */
	preload(src) {
		if (!src || this.audioCache.has(src)) return;
		const audio = new Audio(this._resolveSrc(src));
		audio.preload = 'auto';
		this.audioCache.set(src, audio);
	}

	/** Whether a sound id is currently playing. */
	isPlaying(id) {
		return this.currentlyPlaying.has(id);
	}

	/** Set volume for a currently playing sound. */
	setVolume(id, volume) {
		const audio = this.currentlyPlaying.get(id);
		if (audio) {
			audio.volume = Math.max(0, Math.min(1, volume));
		}
	}
}

// Singleton instance shared across the app.
const soundPlayer = new SoundPlayer();

export default soundPlayer;
